import React, { useState } from 'react';
import { toast } from 'react-toastify';
import IndexedDBService from '../services/IndexedDBService';
import ApiService from '../services/ApiService';

const AdminImport = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [useApi, setUseApi] = useState(true); // Default to API mode

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls'].includes(fileExtension)) {
        setSelectedFile(file);
        toast.info(`Selected file: ${file.name}`);
      } else {
        toast.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        event.target.value = '';
      }
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(10);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 10;
          if (newProgress >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 500);

      let result;
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();

      if (useApi) {
        // Upload to API server (SQLite backend)
        if (fileExtension !== 'csv') {
          clearInterval(progressInterval);
          throw new Error('API mode only supports CSV files. Switch to Client-side mode for Excel files.');
        }
        result = await ApiService.uploadCSV(selectedFile);
        toast.success(`File uploaded to server successfully!`);
      } else {
        // Load to IndexedDB (client-side)
        if (fileExtension === 'csv') {
          result = await IndexedDBService.loadFromFileObject(selectedFile);
        } else if (['xlsx', 'xls'].includes(fileExtension)) {
          result = await IndexedDBService.loadFromExcel(selectedFile);
        } else {
          clearInterval(progressInterval);
          throw new Error('Unsupported file format');
        }
        toast.success(`File uploaded successfully! ${result.recordCount} records processed.`);
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Reset after successful upload
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setSelectedFile(null);
        document.getElementById('file-input').value = '';
      }, 1500);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file: ' + (error.message || 'Unknown error'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle CSV file upload from the provided template
  const handleTemplateUpload = async () => {
    try {
      setUploading(true);
      setUploadProgress(10);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 10;
          if (newProgress >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 500);

      // Create a mock file from the template data
      const csvContent = `symbol,signal_score,predicted_signal_score,predicted_direction,n_days_since_cross_over,new_up_trend,new_down_trend,trend_state,trend_comment,n_last_downtrend_duration,pcnt_upside_in_range_6m,pcnt_upside_in_range_12m,gap_from_6m_high,gap_from_12m_high,duration_range,near_period_vwma20_low,rsi,Quant Ratings,ABC_flag,isEtf,short_fname,long_fname,companyName,industry,sector,returnOnEquityTTM,priceSalesRatioTTM,priceEarningsRatioTTM,priceEarningsToGrowthRatioTTM,grossProfitMarginTTM,netProfitMarginTTM,debtRatioTTM,piotroskiScore,eps_pos_qtrs,revenue_cagr,revenue_pos_trend_yrs,sma_10_200,sma_30_200,sma_50_200,pcnt_from_sma200,pcnt_from_sma100,sma50_slope,sma200_slope,ratingRecommendation,holdings,formattedMetrics,formattedTrendComment
MSTR,5,5,up,14,0,0,1,"Positive, strong",-10,0.138932289,0.138932289,Between 10% and 15% gap,Between 10% and 15% gap," Positive, 6 - 20 days",0,72.01074698,1.47,0,0,MSTR.jpg,/temp/charts/all_p1/bb_MSTR_5_12u_2025-05-11-08-27.jpg,MicroStrategy Incorporated,Software - Application,Technology,-0.82,247.66,-19.98,0.07,0.71,-11.63,0,2,2,-0.01,1,sma_10_200_above,sma_30_200_above,sma_50_200_above,0.52554051,0.272395295,1,1,Strong Sell,,"<div class=""font_10"" style=""display: table; width: 100%;"">
  <div style=""display: table-row;"">
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; ""># Days in Up Trend:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;14</div>
  </div>
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; "">Signal Score:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;5 / 5</div>
  </div>
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; "">AI Predictive Score:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;5 / 5</div>
  </div>
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; "">Gap from 6 month high:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;14%</div>
  </div>
</div>","<div class=""font_10"" style=""text-align: center;"">Positive, strong</div>"
BST,5,5,down,11,0,0,1,"Positive, strong",-38,0.122513693,0.12280196,Between 10% and 15% gap,Between 10% and 15% gap," Positive, 6 - 20 days",0,60.44509694,,,0,BST.jpg,/temp/charts/all_p1/bb_BST_5_09u_2025-05-11-08-27.jpg,BlackRock Science and Technology Trust,Asset Management,Financial Services,0.2,22.59,4.42,3.43,0.75,5.11,0,4,4,1.1,2,sma_10_200_below,sma_30_200_below,sma_50_200_below,-0.025016974,-0.02280681,0,0,Neutral,,"<div class=""font_10"" style=""display: table; width: 100%;"">
  <div style=""display: table-row;"">
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; ""># Days in Up Trend:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;11</div>
  </div>
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; "">Signal Score:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;5 / 5</div>
  </div>
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; "">AI Predictive Score:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;5 / 5</div>
  </div>
  <div style=""display: table-row;"">
    <div style=""display: table-cell; width: 150px; text-align: right; "">Gap from 6 month high:</div>
    <div style=""display: table-cell; width: 40px; text-align: right;"">&nbsp;12%</div>
  </div>
</div>","<div class=""font_10"" style=""text-align: center;"">Positive, strong</div>"`;

      // Create a blob and file from the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'template_data.csv', { type: 'text/csv' });

      // Load template into local IndexedDB
      const result = await IndexedDBService.loadFromFileObject(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      toast.success(`Template data uploaded successfully! ${result.recordCount} records processed.`);

      // Reset after successful upload
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 1500);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload template data: ' + (error.message || 'Unknown error'));
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Admin Data Import
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Import data into local database from CSV or Excel files
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* File Upload Section */}
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Upload Your Data File
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Select a CSV or Excel file (.csv, .xlsx, .xls) to load your stock data.
                </p>

                {/* Storage Mode Toggle */}
                <div className="mb-4 bg-blue-50 p-4 rounded-md border border-blue-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Storage Mode
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="storage-mode"
                        checked={useApi}
                        onChange={() => setUseApi(true)}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        <strong>API Server (SQLite)</strong> - Recommended, persistent storage
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="storage-mode"
                        checked={!useApi}
                        onChange={() => setUseApi(false)}
                        className="mr-2"
                      />
                      <span className="text-sm">
                        <strong>Client-side (IndexedDB)</strong> - Browser storage, supports Excel
                      </span>
                    </label>
                  </div>
                  {useApi && (
                    <p className="text-xs text-blue-700 mt-2">
                      ℹ️ API mode only supports CSV files. Data persists across browser sessions.
                    </p>
                  )}
                  {!useApi && (
                    <p className="text-xs text-yellow-700 mt-2">
                      ⚠️ Client-side data is stored in your browser and may be cleared if you clear site data.
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="file-input" className="block text-sm font-medium text-gray-700 mb-2">
                      Choose File
                    </label>
                    <input
                      id="file-input"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      disabled={uploading}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                    />
                  </div>

                  {selectedFile && (
                    <div className="bg-blue-50 p-3 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                  )}

                  {!uploading ? (
                    <button
                      onClick={handleFileUpload}
                      disabled={!selectedFile}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {useApi ? 'Upload to API Server' : 'Load into Browser Storage'}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 text-center">
                        Uploading file... {uploadProgress}%
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Template Data Section */}
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Import Template Data
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click the button below to import the sample data from the built-in template into the local database.
                </p>

                {!uploading ? (
                  <button
                    onClick={handleTemplateUpload}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Import Template Data
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 text-center">
                      Importing data... {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Database Information */}
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Storage Options
                </h3>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      API Server Mode (SQLite)
                    </p>
                    <p className="text-sm text-gray-700">
                      Data stored in a SQLite database on your computer. Persistent, fast, and accessible across browser sessions. Requires the API server to be running (port 3001).
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      Client-side Mode (IndexedDB)
                    </p>
                    <p className="text-sm text-gray-700">
                      Data stored in your browser's IndexedDB. Works offline, supports Excel files, but may be cleared if you clear browser data.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Supported File Formats
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                  <li><strong>CSV files (.csv)</strong> - Comma-separated values</li>
                  <li><strong>Excel files (.xlsx, .xls)</strong> - Microsoft Excel spreadsheets</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">
                  The first row should contain column headers that match your data structure.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Next Steps
                </h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                  <li>Upload your data file using the form above, or import template data</li>
                  <li>Go to the Screener to view the imported data</li>
                  <li>Use the filter menu to filter and explore your stock data</li>
                  <li>To inspect stored data, open DevTools → Application → IndexedDB → StockFilterDB</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminImport;
